import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users } from "lucide-react";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { useLanguage } from "@/hooks/use-language";

// Community Standards Page (Full Content)
const CommunityStandardsPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const hideHeader = searchParams.get("header") === "false";
  const hideFooter = searchParams.get("footer") === "false";

  return (
    <div className="bg-[#030014] min-h-screen overflow-x-hidden">
      {!hideHeader && <Header />}
      <main className={hideHeader ? "pt-8 pb-20" : "pt-24 pb-20"}>
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          {/* Back Button */}
          {!hideHeader && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link
                to="/policy"
                className="inline-flex items-center gap-2 text-white/60 hover:text-[#FF2E63] transition-colors mb-8"
                data-testid="back-policy-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                {t("nav.backToPolicies")}
              </Link>
            </motion.div>
          )}

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF2E63]/20 to-pink-600/10 flex items-center justify-center">
                <Users className="w-7 h-7 text-[#FF2E63]" />
              </div>
              <div>
                <p className="text-[#FF2E63] text-xs tracking-[0.3em] uppercase">
                  {t("policy.legal")}
                </p>
                <h1 className="font-display font-bold text-3xl sm:text-4xl text-white">
                  {t("policy.communityStandards")}
                </h1>
              </div>
            </div>
            <p className="text-white/50">{t("policy.lastUpdated")}</p>
          </motion.div>

          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-10 p-6 rounded-2xl bg-white/5 border border-white/10"
          >
            <p className="text-white/80 leading-relaxed">
              PHUONG is built to be a safe, respectful, and enjoyable platform
              for all users. These Community Standards apply to all content,
              interactions, and AI character models across{" "}
              <strong>PHUNSOCIAL</strong>, <strong>PHUNMUSIC</strong>, the{" "}
              <strong>Creator Marketplace</strong>, and all other areas of the
              PHUONG ecosystem. Violations are handled strictly and may result
              in permanent removal from the platform.
            </p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-10 text-white/80 leading-relaxed"
          >
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                1. Sexual Content
              </h2>
              <p className="mb-4">
                PHUONG is an adult platform, intended for users who meet the
                minimum age required by the applicable app store and applicable
                law. The following applies to all user-generated content and
                creator models:
              </p>

              <div className="mb-4">
                <p className="font-semibold text-white mb-2">Permitted:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Artistic, illustrated, or animated mature content
                    appropriate for an adult (18+) audience, provided it is
                    properly categorized and tagged where applicable.
                  </li>
                </ul>
              </div>

              <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/30">
                <p className="font-semibold text-white mb-3">
                  Strictly Prohibited:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Child Sexual Abuse Material (CSAM) or any sexual content
                    depicting or implying minors, whether real, illustrated,
                    AI-generated, or fictional.{" "}
                    <strong>
                      We have a zero-tolerance policy. Any detected content will
                      be reported immediately to the National Center for Missing
                      & Exploited Children (NCMEC) and relevant law enforcement
                      authorities.
                    </strong>
                  </li>
                  <li>
                    Real-world pornography or sexually explicit depictions of
                    real, identifiable people.
                  </li>
                  <li>
                    Non-consensual sexual content, including deepfakes or
                    synthetic media produced without the subject's consent.
                  </li>
                  <li>
                    Sexual content that demeans, dehumanizes, or targets
                    individuals without consent.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                2. Violence & Harmful Content
              </h2>
              <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/30">
                <p className="font-semibold text-white mb-3">Prohibited:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Content that glorifies, promotes, or instructs on real-world
                    violence, self-harm, suicide, or eating disorders.
                  </li>
                  <li>
                    Content that encourages, recruits for, or celebrates
                    terrorism, extremism, or mass violence.
                  </li>
                  <li>
                    Gratuitous or graphic depictions of real-world violence
                    intended to shock or disturb.
                  </li>
                  <li>Content depicting or promoting the abuse of animals.</li>
                  <li>
                    Threats of violence against specific individuals or groups.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                3. Hate Speech & Discrimination
              </h2>
              <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/30">
                <p className="font-semibold text-white mb-3">Prohibited:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Hate speech, slurs, or content that dehumanizes individuals
                    or groups based on race, ethnicity, nationality, religion,
                    gender, sexual orientation, gender identity, disability, or
                    other protected characteristics.
                  </li>
                  <li>
                    Content that portrays any group as subhuman or advocates for
                    discrimination.
                  </li>
                  <li>
                    Symbols, imagery, or language associated with hate groups or
                    extremist ideologies.
                  </li>
                  <li>
                    Bullying, targeted harassment, or coordinated attacks
                    against specific individuals or communities.
                  </li>
                  <li>
                    Doxxing — the public exposure of another person's private,
                    personally identifying information without their consent.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                4. Politically Prohibited & Anti-Government Content
              </h2>
              <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/30">
                <p className="font-semibold text-white mb-3">
                  Strictly Prohibited:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Content that opposes, slanders, undermines, or incites
                    action against the Socialist Republic of Vietnam, its
                    government, leadership, national security, or policies.
                  </li>
                  <li>
                    Content that violates Vietnamese law, including but not
                    limited to the Cybersecurity Law, Press Law, and regulations
                    on online content.
                  </li>
                  <li>
                    Content that spreads political propaganda, misinformation,
                    or disinformation intended to destabilize public order or
                    state authority.
                  </li>
                  <li>
                    Content that calls for or glorifies separatism, sedition, or
                    armed resistance against any government.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                5. Fraud, Scams & Deceptive Behavior
              </h2>
              <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/30">
                <p className="font-semibold text-white mb-3">Prohibited:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Scamming, defrauding, or financially exploiting other users
                    in any form.
                  </li>
                  <li>
                    Phishing — creating content or interactions designed to
                    steal user credentials, financial information, or personal
                    data.
                  </li>
                  <li>
                    Impersonating PHUONG staff, administrators, other users, or
                    any public figure in a misleading or deceptive manner.
                  </li>
                  <li>
                    Spreading deliberate misinformation or disinformation
                    intended to deceive or manipulate users.
                  </li>
                  <li>
                    Pyramid schemes, multi-level marketing solicitation, or any
                    unsanctioned commercial solicitation within the platform.
                  </li>
                  <li>
                    Creating AI models or content designed to deceive users into
                    harmful actions or to extract value through manipulation.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                6. Platform Integrity & System Harm
              </h2>
              <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/30">
                <p className="font-semibold text-white mb-3">Prohibited:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Attempting to hack, exploit vulnerabilities, reverse
                    engineer, or disrupt PHUONG's systems or infrastructure.
                  </li>
                  <li>
                    Prompt injection attacks — attempting to manipulate PHUONG
                    AI into bypassing safety guidelines or producing prohibited
                    content.
                  </li>
                  <li>
                    Using bots, scripts, or automation to spam, flood, or
                    manipulate the platform.
                  </li>
                  <li>
                    Operating multiple accounts or creating a new account after
                    being banned.
                  </li>
                  <li>
                    Any conduct intended to damage PHUONG's reputation,
                    operations, or user trust.
                  </li>
                  <li>
                    Interfering with other users' access to or enjoyment of the
                    platform.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                7. Creator Marketplace &amp; Music Standards
              </h2>
              <p className="mb-4">
                In addition to all standards above, AI character models submitted
                to the Creator Marketplace, and music content (tracks, lyrics,
                cover art) submitted to PHUNMUSIC, must:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  Comply with all content prohibitions listed in Sections 1–6.
                </li>
                <li>
                  Not impersonate real, identifiable individuals, celebrities,
                  or public figures in a defamatory, misleading, or harmful
                  manner.
                </li>
                <li>
                  Not incorporate third-party intellectual property (copyrighted
                  characters, trademarks, etc.) without verifiable
                  authorization.
                </li>
                <li>
                  Accurately and honestly describe the model's capabilities,
                  intended use, and content rating.
                </li>
                <li>
                  Be appropriately tagged and categorized, particularly for
                  mature-rated models.
                </li>
                <li>
                  Not be designed or configured to manipulate, deceive, or harm
                  the users who interact with them.
                </li>
                <li>
                  Not contain hidden functionality, malicious code, or embedded
                  mechanisms intended to extract data or perform unauthorized
                  actions.
                </li>
              </ul>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white/70 text-sm">
                  Administrators may disable any model without prior notice if
                  it is found to violate these standards. Disabled models are
                  not eligible for refunds of any kind.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                8. Reporting & Enforcement
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Reporting Violations
                  </h3>
                  <p>
                    Use the in-app "Report" button to flag content or users. For
                    urgent matters — including CSAM, credible threats of
                    violence, or ongoing exploitation — contact us immediately
                    at{" "}
                    <a
                      href="mailto:contact@phuongai.com"
                      className="text-[#FF2E63] hover:underline"
                    >
                      contact@phuongai.com
                    </a>
                    .
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Review Process
                  </h3>
                  <p>
                    Our moderation team reviews all reports, assisted by
                    automated content detection. We aim to review reports
                    promptly, with priority given to reports involving minors,
                    violence, or serious harm.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Enforcement Actions
                  </h3>
                  <p className="mb-2">
                    Depending on the severity and frequency of violations,
                    enforcement actions may include:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Content removal</li>
                    <li>Formal warning</li>
                    <li>Temporary account suspension</li>
                    <li>Permanent account ban (including device-level ban)</li>
                    <li>
                      Indefinite model disabling on the Creator Marketplace
                    </li>
                    <li>
                      Reporting to law enforcement (for CSAM, credible threats,
                      or serious criminal offenses)
                    </li>
                  </ul>
                </div>

                <div className="p-5 rounded-xl bg-red-500/10 border border-red-500/30">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Appeals
                  </h3>
                  <p className="text-white/80 text-sm">
                    There is{" "}
                    <strong>no appeal process for permanent bans</strong>{" "}
                    resulting from CSAM violations or verified criminal
                    activity. For other bans, you may submit an appeal by
                    contacting{" "}
                    <a
                      href="mailto:contact@phuongai.com"
                      className="text-[#FF2E63] hover:underline"
                    >
                      contact@phuongai.com
                    </a>
                    . Appeals are reviewed at our sole discretion, and decisions
                    are final.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                Questions or Concerns?
              </h2>
              <p className="mb-4">
                If you have questions about these Community Standards or need to
                report a violation, please contact us at:
              </p>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-white/60 mb-2">Email:</p>
                <a
                  href="mailto:contact@phuongai.com"
                  className="text-[#FF2E63] hover:underline text-lg font-medium"
                >
                  contact@phuongai.com
                </a>
              </div>
            </section>
          </motion.div>
        </div>
      </main>
      {!hideFooter && <Footer />}
    </div>
  );
};

export default CommunityStandardsPage;
